import { useEffect } from 'react';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

/**
 * Confirmation toast for tap logging. Module-local copy of the recipes module's toast
 * (Component - Toast and Notifications.dc.html) — there is still no shared toast system,
 * and modules don't reach into each other's components.
 *
 * A tap is a one-tap write with no confirm step, so **undo is what makes that safe**: the
 * toast is the only place a mis-tap can be taken back, which is why the action is offered
 * for a generous six seconds rather than the usual flash-and-gone.
 */
export function Toast({
  title,
  subtitle,
  action,
  onDismiss,
  durationMs = 6000,
}: {
  title: string;
  subtitle?: string;
  action?: ToastAction;
  onDismiss: () => void;
  durationMs?: number;
}): JSX.Element {
  useEffect(() => {
    if (durationMs <= 0) return;
    const timer = setTimeout(onDismiss, durationMs);
    return () => clearTimeout(timer);
  }, [durationMs, onDismiss]);

  return (
    <div
      role="status"
      data-testid="tracker-toast"
      className="fixed inset-x-4 bottom-[calc(72px+env(safe-area-inset-bottom))] z-30 mx-auto flex max-w-sm items-center gap-3 rounded-lg bg-inverse px-4 py-3 text-inverse-fg shadow-lg ease-app lg:bottom-4 lg:left-4 lg:right-auto"
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold">{title}</div>
        {subtitle && <div className="truncate text-xs opacity-70">{subtitle}</div>}
      </div>
      {action && (
        <button
          type="button"
          data-testid="tracker-toast-action"
          onClick={() => {
            action.onClick();
            onDismiss();
          }}
          className="shrink-0 rounded-md px-2 py-1.5 text-sm font-bold text-accent"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
